/**
 * Helper hooks to replace Next.js navigation hooks with React Router equivalents
 * This provides a compatibility layer for easier migration
 */

import { useNavigate, useLocation, useSearchParams as useSearchParamsRouter } from 'react-router-dom'

// Replace useRouter from next/navigation
export function useRouter() {
  const navigate = useNavigate()
  const location = useLocation()

  return {
    push: (path: string) => navigate(path),
    replace: (path: string) => navigate(path, { replace: true }),
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => window.location.reload(),
    pathname: location.pathname,
    query: Object.fromEntries(new URLSearchParams(location.search)),
  }
}

// Replace usePathname from next/navigation
export function usePathname() {
  const location = useLocation()
  return location.pathname
}

// Replace useSearchParams from next/navigation
// Returns a tuple like Next.js: [searchParams, setSearchParams]
export function useSearchParamsCompat(): [URLSearchParams, (params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams), navigateOptions?: { replace?: boolean }) => void] {
  const [searchParams, setSearchParams] = useSearchParamsRouter()
  
  const setParams = (params: URLSearchParams | ((prev: URLSearchParams) => URLSearchParams), options?: { replace?: boolean }) => {
    if (typeof params === 'function') {
      setSearchParams(params, options)
    } else {
      setSearchParams(params, options)
    }
  }
  
  return [searchParams, setParams]
}

// Alternative: Return object with get/set methods (like Next.js object API)
export function useSearchParamsObject() {
  const [searchParams, setSearchParams] = useSearchParamsRouter()
  
  return {
    get: (key: string) => searchParams.get(key),
    getAll: (key: string) => searchParams.getAll(key),
    has: (key: string) => searchParams.has(key),
    keys: () => Array.from(searchParams.keys()),
    values: () => Array.from(searchParams.values()),
    entries: () => Array.from(searchParams.entries()),
    toString: () => searchParams.toString(),
    set: (key: string, value: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.set(key, value)
      setSearchParams(newParams)
    },
    delete: (key: string) => {
      const newParams = new URLSearchParams(searchParams)
      newParams.delete(key)
      setSearchParams(newParams)
    },
  }
}
